import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dialogActions from 'core/dialogs/actions';
import RawHtml from 'common/RawHtml';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import Group from 'containers/Group';
import Field from 'containers/Field';
import pageActions from 'core/page/actions';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';

import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import headerStyles from './styles/header';
import titleStyles from './styles/title';
import contentWrapperStyles from './styles/contentWrapper';
import bodyStyles from './styles/body';
import footerStyles from './styles/footer';
import errorStyles from './styles/error';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: contentStyles(theme),
    contentWrapper: contentWrapperStyles(theme),
    dialog: dialogStyles(theme),
    error: errorStyles(theme),
    footer: footerStyles(theme),
    header: headerStyles(theme),
    title: titleStyles(theme),
  };
  return {
    ...common,
    body: {
      ...common.body,
      '& > div': {
        background: 'white',
      },
      minHeight: 'inherit',
    },
    intro: {},
    panel: {
      '& .btn-row-group': {
        display: 'none',
      },
      '& h1': {
        fontWeight: theme.typography.fontWeights.bold,
        margin: [0, 0, theme.spacing.xs, 0],
      },
      '&.active': {
        display: 'block',
      },
      display: 'none',
    },
  };
};

const GROUP_NAME = 'HOME_FUNDING_VERIFY';

const renderFields = (group) => {
  const hasFields = group.fields && group.fields.length > 0;

  if (hasFields) {
    return group.fields.map((fieldName) => (
      <Field key={fieldName} fieldName={fieldName} groupName={group.name} />
    ));
  }

  return null;
};

export const HomeVerifyDepositsBare = ({
  classes,
  close,
  commonClasses,
  depositsVerifyLabel,
  disclosureCancelLabel,
  disclosureCloseLabel,
  group,
  params,
  resources,
  session,
  verifyFunding,
}) => {
  const { request, status } = params;
  const buttons =
    status.success !== null
      ? [
          {
            className: 'btn btn-mobile btn-i-accept',
            onClick: close,
            text: disclosureCloseLabel,
            variant: 'primary',
          },
        ]
      : [
          {
            className: classnames('btn btn-mobile btn-i-accept', {
              loading: status.loading === true,
            }),
            onClick: verifyFunding,
            text: depositsVerifyLabel,
            variant: 'primary',
          },
          {
            className: 'btn-mobile btn-close btn-mobile--secondary',
            onClick: close,
            text: disclosureCancelLabel,
            variant: 'secondary',
          },
        ];

  return (
    <div className={classnames('modal-verify-deposits', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-header', classes.header)}>
          <h4 className={classnames('modal-title', classes.title)} id="modal-title">
            {resources.fundingVerificationPopupTitle}
          </h4>
        </div>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div className={classnames('modal-content-wrapper', classes.contentWrapper)}>
            <div className="verify-deposits-dialog-view">
              <div
                className={classnames('panel intro', classes.panel, classes.intro, {
                  active: status.success === null,
                })}
              >
                <RawHtml>{request.fundingVerificationSubtitle}</RawHtml>

                {status.errorMsg && (
                  <div className="row">
                    <div className="widget text-center">
                      <ErrorMsg error errorMsg={status.errorMsg} type="page" />
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="widget">
                    <div className="region-groups">
                      <Group name={GROUP_NAME} forceActiveGroup={group.name}>
                        {renderFields(group)}
                      </Group>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={classnames('panel success', classes.panel, classes.success, {
                  active: status.success === true,
                })}
              >
                <h1>{resources.fundingVerificationPopupSuccessTitle}</h1>

                <RawHtml>{request.fundingVerificationSuccessSubTitle}</RawHtml>
              </div>
              <div
                className={classnames('panel failure', classes.panel, classes.failure, {
                  active: status.success === false,
                })}
              >
                <RawHtml type="h1">{resources.fundingVerificationPopupFailureTitle}</RawHtml>

                <RawHtml>{resources.fundingVerificationPopupFailureSubTitle}</RawHtml>

                <hr />

                <p style={{ textAlign: 'center' }}>
                  <strong>Customer Service</strong>
                  <br />
                  Phone: {session.clientPhone}
                  <br />
                  Email: {session.clientEmail}
                  <br />
                  <a href={session.clientWeb} target="_blank" rel="noopener noreferrer">
                    {session.clientWeb}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)}>
          {buttons.map((b, i) => (
            <Button
              /* eslint-disable-next-line react/no-array-index-key */
              key={`dialog-button-${i}`}
              className={`btn ${b.className} popbox-btn${i}`}
              data-key={i}
              onClick={b.onClick}
              disabled={status.loading}
              variant={b.variant}
            >
              {b.text}
              {b.className.includes('btn-i-accept') && (
                <>
                  <i className="icon-arrow-thin-right" aria-hidden="true" />
                  <Spinner loading={status.loading} />
                </>
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

HomeVerifyDepositsBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  depositsVerifyLabel: PropTypes.string,
  disclosureCancelLabel: PropTypes.string,
  disclosureCloseLabel: PropTypes.string,
  group: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  params: PropTypes.shape({
    request: PropTypes.shape({
      fundingVerificationSubtitle: PropTypes.string,
      fundingVerificationSuccessSubTitle: PropTypes.string,
    }),
    status: PropTypes.string,
  }).isRequired,
  resources: PropTypes.shape({
    fundingVerificationPopupFailureSubTitle: PropTypes.string,
    fundingVerificationPopupFailureTitle: PropTypes.string,
    fundingVerificationPopupSuccessTitle: PropTypes.string,
    fundingVerificationPopupTitle: PropTypes.string,
  }).isRequired,
  session: PropTypes.shape({
    clientEmail: PropTypes.string,
    clientPhone: PropTypes.string,
    clientWeb: PropTypes.string,
  }).isRequired,
  verifyFunding: PropTypes.func.isRequired,
};

HomeVerifyDepositsBare.defaultProps = {
  depositsVerifyLabel: 'Verify',
  disclosureCancelLabel: 'Cancel',
  disclosureCloseLabel: 'Close',
};

const mapStateToProps = ({ dialogs, groups, resources, session }) => ({
  depositsVerifyLabel: resources.depositsVerifyLabel,
  disclosureCancelLabel: resources.disclosureCancelLabel,
  disclosureCloseLabel: resources.disclosureCloseLabel,
  disclosureVerifyLabel: resources.disclosureVerifyLabel,
  group: groups[GROUP_NAME],
  params: dialogs.params,
  resources,
  session: session.data,
});

const mapDispatchToProps = (dispatch) => ({
  close: () => {
    dispatch(dialogActions.close());
    dispatch(pageActions.getCurrent());
  },
  verifyFunding: () => {
    dispatch(pageActions.verifyFunding());
  },
});

const HomeVerifyDepositsStyled = withStyles(styles)(HomeVerifyDepositsBare);

export default connect(mapStateToProps, mapDispatchToProps)(HomeVerifyDepositsStyled);
